import { useNavigate } from "@tanstack/react-router";
import { memo, useCallback } from "react";
import Close from "~icons/material-symbols/close";
import { css } from "../../../../styled-system/css";
import bousaiMiyapo from "../../../assets/miyagi/bousai-miyapo.png";
import { MIYAGI_BOUSAI_SERVICE_ID } from "../../../config";
import type { TenantWithFeature } from "../../../libs/connect";
import { t } from "../../../libs/i18n";
import { encodeUUID } from "../../../libs/ps";
import { isMockEnv } from "../../../libs/utils";

type ModalProps = {
  tenant: TenantWithFeature;
  onClickCancel: () => void;
};

export const LotteryModal = memo(({ onClickCancel, tenant }: ModalProps) => {
  const navigate = useNavigate();

  const handleOutsideClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    // クリックがバックドロップ（背景）のみであれば、閉じる処理を実行
    if (e.target === e.currentTarget) {
      onClickCancel();
    }
  };

  const DefaultLotteryModal = () => {
    return (
      <div
        className={css({
          bg: "white",
          roundedTop: "2xl",
          px: "16px",
          pt: "16px",
          pb: "48px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 999,
        })}
      >
        <div>
          <div
            className={css({
              display: "flex",
            })}
          >
            <div
              className={css({
                color: "text.primary",
                fontSize: "18px",
                fontWeight: 500,
              })}
            >
              {t("ポイント抽選に参加しましょう！")}
            </div>

            <button
              type="button"
              className={css({
                color: "text.primary",
                border: "none",
                cursor: "pointer",
                ml: "auto",
                bg: "action.hover",
                rounded: "full",
                p: "4px",
              })}
              onClick={onClickCancel}
            >
              <Close />
            </button>
          </div>
        </div>
        <p>{t("その場でポイント抽選の結果を確認することができます。")}</p>

        <div
          className={css({ display: "flex", gap: "10px", flexDir: "column" })}
        >
          <button
            type="button"
            className={css({
              bg: "surface.accentPrimary",
              color: "white",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={() => {
              navigate({ to: "/lottery/processing" });
              onClickCancel();
            }}
          >
            {t("抽選に参加")}
          </button>
          <button
            type="button"
            className={css({
              bg: "rgba(229, 231, 235, 0.50)",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={onClickCancel}
          >
            {t("あとで")}
          </button>
        </div>
      </div>
    );
  };

  const MiyagiLotteryModal = () => {
    const handleClickApplyWithBousai = useCallback(() => {
      // 経路を保存
      localStorage.setItem("bousai_callback_source", "miyagi");

      if (isMockEnv()) {
        navigate({ to: "/lottery/bousai/mock" });
      } else {
        location.href = `https://p8n.jp/i/${encodeUUID(MIYAGI_BOUSAI_SERVICE_ID)}?u=${encodeURIComponent("#/campaign/miyagi-point")}`;
      }

      onClickCancel();
    }, [onClickCancel]);

    return (
      <div
        className={css({
          bg: "white",
          roundedTop: "2xl",
          px: "16px",
          pt: "16px",
          pb: "48px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 999,
        })}
      >
        <div>
          <div
            className={css({
              display: "flex",
            })}
          >
            <div
              className={css({
                color: "text.primary",
                fontSize: "18px",
                fontWeight: 500,
              })}
            >
              {t("ポイント抽選に参加しましょう！")}
            </div>

            <button
              type="button"
              className={css({
                color: "text.primary",
                border: "none",
                cursor: "pointer",
                ml: "auto",
                bg: "action.hover",
                rounded: "full",
                p: "4px",
              })}
              onClick={onClickCancel}
            >
              <Close />
            </button>
          </div>
          <p
            className={css({
              color: "text.secondary",
              fontSize: "14px",
            })}
          >
            {t("まずは「みやぎ防災ミニアプリ」に登録")}
          </p>
        </div>
        <p>
          {t(
            "ポイント抽選への参加はみやぎ防災ミニアプリの登録が必要です。登録完了後、その場でポイント抽選の結果を確認することができます。",
          )}
        </p>
        <div
          className={css({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          })}
        >
          <img
            src={bousaiMiyapo}
            alt={t("防災アプリに登録")}
            className={css({
              w: "176px",
            })}
          />
        </div>
        <div
          className={css({ display: "flex", gap: "10px", flexDir: "column" })}
        >
          <button
            type="button"
            className={css({
              bg: "surface.accentPrimary",
              color: "white",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={handleClickApplyWithBousai}
          >
            {t("防災に登録して抽選に参加")}
          </button>
          <button
            type="button"
            className={css({
              bg: "rgba(229, 231, 235, 0.50)",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={onClickCancel}
          >
            {t("あとで")}
          </button>
        </div>
      </div>
    );
  };

  const ShigaOtsuLotteryModal = () => {
    const handleClickGrant = useCallback(() => {
      navigate({ to: "/lottery/grant/processing" });
      onClickCancel();
    }, [onClickCancel]);

    return (
      <div
        className={css({
          bg: "white",
          roundedTop: "2xl",
          px: "16px",
          pt: "16px",
          pb: "48px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 999,
        })}
      >
        <div>
          <div
            className={css({
              display: "flex",
            })}
          >
            <div
              className={css({
                color: "text.primary",
                fontSize: "18px",
                fontWeight: 500,
              })}
            >
              {t("新規登録キャンペーンに応募しましょう")}
            </div>

            <button
              type="button"
              className={css({
                color: "text.primary",
                border: "none",
                cursor: "pointer",
                ml: "auto",
                bg: "action.hover",
                rounded: "full",
                p: "4px",
              })}
              onClick={onClickCancel}
            >
              <Close />
            </button>
          </div>
        </div>
        <p
          className={css({
            color: "text.secondary",
            fontSize: "14px",
            lineHeight: "1.6",
          })}
        >
          {t(
            "応募はおひとりさま1回まで、応募するとその場で300ポイントが付与されます。",
          )}
        </p>

        <div
          className={css({ display: "flex", gap: "10px", flexDir: "column" })}
        >
          <button
            type="button"
            className={css({
              bg: "surface.accentPrimary",
              color: "white",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={handleClickGrant}
          >
            {t("応募する")}
          </button>
          <button
            type="button"
            className={css({
              bg: "rgba(229, 231, 235, 0.50)",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={onClickCancel}
          >
            {t("あとで")}
          </button>
        </div>
      </div>
    );
  };

  const MarumoriLotteryModal = () => {
    const handleClickApply = useCallback(() => {
      navigate({ to: "/lottery/processing" });
      onClickCancel();
    }, [onClickCancel]);

    return (
      <div
        className={css({
          bg: "white",
          roundedTop: "2xl",
          px: "16px",
          pt: "16px",
          pb: "48px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 999,
        })}
      >
        <div>
          <div
            className={css({
              display: "flex",
            })}
          >
            <div
              className={css({
                color: "text.primary",
                fontSize: "18px",
                fontWeight: 500,
              })}
            >
              {t("ポイント抽選に参加しましょう！")}
            </div>

            <button
              type="button"
              className={css({
                color: "text.primary",
                border: "none",
                cursor: "pointer",
                ml: "auto",
                bg: "action.hover",
                rounded: "full",
                p: "4px",
              })}
              onClick={onClickCancel}
            >
              <Close />
            </button>
          </div>
        </div>
        <p>{t("その場でポイント抽選の結果を確認することができます。")}</p>

        <div
          className={css({ display: "flex", gap: "10px", flexDir: "column" })}
        >
          <button
            type="button"
            className={css({
              bg: "surface.accentPrimary",
              color: "white",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={handleClickApply}
          >
            {t("抽選に参加")}
          </button>
          <button
            type="button"
            className={css({
              bg: "rgba(229, 231, 235, 0.50)",
              rounded: "2xl",
              p: "12px",
            })}
            onClick={onClickCancel}
          >
            {t("あとで")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={css({
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bg: "rgba(0, 0, 0, 0.1)",
        zIndex: 888,
      })}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        className={css({
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 999,
          backdropFilter: "blur(3px)",
          transition: "transform 0.3s ease-out",
          transform: "translateY(100%)", // Initially hidden
          animation: "slide-up 0.3s forwards", // Apply slide-up animation
        })}
        onClick={handleOutsideClick}
      >
        {tenant.slug === "miyagi" ? (
          <MiyagiLotteryModal />
        ) : tenant.slug === "marumori" ? (
          <MarumoriLotteryModal />
        ) : tenant.slug === "shiga-otsu" ? (
          <ShigaOtsuLotteryModal />
        ) : (
          <DefaultLotteryModal />
        )}
      </div>
    </div>
  );
});

type KesennumaLotteryModalProps = {
  onClickCancel: () => void;
};

export const KesennumaLotteryModal = memo(
  ({ onClickCancel }: KesennumaLotteryModalProps) => {
    const navigate = useNavigate();

    const handleOutsideClick = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      if (e.target === e.currentTarget) {
        onClickCancel();
      }
    };

    const handleClickApplyWithBousai = useCallback(() => {
      // 経路を保存
      localStorage.setItem("bousai_callback_source", "kesennuma");

      if (isMockEnv()) {
        navigate({ to: "/lottery/bousai/mock" });
      } else {
        location.href = `https://p8n.jp/i/${encodeUUID(MIYAGI_BOUSAI_SERVICE_ID)}?u=${encodeURIComponent("#/campaign/miyagi-point")}`;
      }

      onClickCancel();
    }, [onClickCancel, navigate]);

    return (
      <div
        className={css({
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bg: "rgba(0, 0, 0, 0.1)",
          zIndex: 888,
        })}
      >
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <div
          className={css({
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 999,
            backdropFilter: "blur(3px)",
            transition: "transform 0.3s ease-out",
            transform: "translateY(100%)",
            animation: "slide-up 0.3s forwards",
          })}
          onClick={handleOutsideClick}
        >
          <div
            className={css({
              bg: "white",
              roundedTop: "2xl",
              px: "16px",
              pt: "16px",
              pb: "48px",
              display: "flex",
              flexDir: "column",
              gap: "16px",
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 999,
            })}
          >
            <div>
              <div
                className={css({
                  display: "flex",
                })}
              >
                <div
                  className={css({
                    color: "text.primary",
                    fontSize: "18px",
                    fontWeight: 500,
                  })}
                >
                  {t("けせんぬま生活応援ポイントに応募しましょう！")}
                </div>

                <button
                  type="button"
                  className={css({
                    color: "text.primary",
                    border: "none",
                    cursor: "pointer",
                    ml: "auto",
                    bg: "action.hover",
                    rounded: "full",
                    p: "4px",
                  })}
                  onClick={onClickCancel}
                >
                  <Close />
                </button>
              </div>
              <p
                className={css({
                  color: "text.secondary",
                  fontSize: "14px",
                })}
              >
                {t("まずは「みやぎ防災ミニアプリ」に登録")}
              </p>
            </div>
            <p>
              {t(
                "ポイント応募にはみやぎ防災ミニアプリの登録が必要です。登録完了後、その場でポイントが付与されます。",
              )}
            </p>
            <div
              className={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              })}
            >
              <img
                src={bousaiMiyapo}
                alt={t("防災アプリに登録")}
                className={css({
                  w: "176px",
                })}
              />
            </div>
            <div
              className={css({
                display: "flex",
                gap: "10px",
                flexDir: "column",
              })}
            >
              <button
                type="button"
                className={css({
                  bg: "surface.accentPrimary",
                  color: "white",
                  rounded: "2xl",
                  p: "12px",
                })}
                onClick={handleClickApplyWithBousai}
              >
                {t("防災に登録して応募する")}
              </button>
              <button
                type="button"
                className={css({
                  bg: "rgba(229, 231, 235, 0.50)",
                  rounded: "2xl",
                  p: "12px",
                })}
                onClick={onClickCancel}
              >
                {t("あとで")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

type MiyagiBousaiWelcomeLotteryModalProps = {
  onClickCancel: () => void;
};

export const MiyagiBousaiWelcomeLotteryModal = memo(
  ({ onClickCancel }: MiyagiBousaiWelcomeLotteryModalProps) => {
    const navigate = useNavigate();

    const handleOutsideClick = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      if (e.target === e.currentTarget) {
        onClickCancel();
      }
    };

    const handleClickApplyWithBousai = useCallback(() => {
      // 経路を保存
      localStorage.setItem("bousai_callback_source", "miyagi-bousai-welcome");

      if (isMockEnv()) {
        navigate({ to: "/lottery/bousai/mock" });
      } else {
        location.href = `https://p8n.jp/i/${encodeUUID(MIYAGI_BOUSAI_SERVICE_ID)}?u=${encodeURIComponent("#/campaign/miyagi-point")}`;
      }

      onClickCancel();
    }, [onClickCancel, navigate]);

    return (
      <div
        className={css({
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bg: "rgba(0, 0, 0, 0.1)",
          zIndex: 888,
        })}
      >
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <div
          className={css({
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 999,
            backdropFilter: "blur(3px)",
            transition: "transform 0.3s ease-out",
            transform: "translateY(100%)",
            animation: "slide-up 0.3s forwards",
          })}
          onClick={handleOutsideClick}
        >
          <div
            className={css({
              bg: "white",
              roundedTop: "2xl",
              px: "16px",
              pt: "16px",
              pb: "48px",
              display: "flex",
              flexDir: "column",
              gap: "16px",
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 999,
            })}
          >
            <div>
              <div
                className={css({
                  display: "flex",
                })}
              >
                <div
                  className={css({
                    color: "text.primary",
                    fontSize: "18px",
                    fontWeight: 500,
                  })}
                >
                  {t("「みやぎ防災」ウェルカムポイントに応募しましょう！")}
                </div>

                <button
                  type="button"
                  className={css({
                    color: "text.primary",
                    border: "none",
                    cursor: "pointer",
                    ml: "auto",
                    bg: "action.hover",
                    rounded: "full",
                    p: "4px",
                  })}
                  onClick={onClickCancel}
                >
                  <Close />
                </button>
              </div>
              <p
                className={css({
                  color: "text.secondary",
                  fontSize: "14px",
                })}
              >
                {t("まずは「みやぎ防災ミニアプリ」に登録")}
              </p>
            </div>
            <p>
              {t(
                "ポイント応募にはみやぎ防災ミニアプリの登録が必要です。登録完了後、その場でポイントが付与されます。",
              )}
            </p>
            <div
              className={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              })}
            >
              <img
                src={bousaiMiyapo}
                alt={t("防災アプリに登録")}
                className={css({
                  w: "176px",
                })}
              />
            </div>
            <div
              className={css({
                display: "flex",
                gap: "10px",
                flexDir: "column",
              })}
            >
              <button
                type="button"
                className={css({
                  bg: "surface.accentPrimary",
                  color: "white",
                  rounded: "2xl",
                  p: "12px",
                })}
                onClick={handleClickApplyWithBousai}
              >
                {t("防災に登録して応募する")}
              </button>
              <button
                type="button"
                className={css({
                  bg: "rgba(229, 231, 235, 0.50)",
                  rounded: "2xl",
                  p: "12px",
                })}
                onClick={onClickCancel}
              >
                {t("あとで")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

type ShiroishiLotteryModalProps = {
  onClickCancel: () => void;
};

export const ShiroishiLotteryModal = memo(
  ({ onClickCancel }: ShiroishiLotteryModalProps) => {
    const navigate = useNavigate();

    const handleOutsideClick = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      if (e.target === e.currentTarget) {
        onClickCancel();
      }
    };

    const handleClickApplyWithBousai = useCallback(() => {
      // 経路を保存
      localStorage.setItem("bousai_callback_source", "shiroishi");

      if (isMockEnv()) {
        navigate({ to: "/lottery/bousai/mock" });
      } else {
        location.href = `https://p8n.jp/i/${encodeUUID(MIYAGI_BOUSAI_SERVICE_ID)}?u=${encodeURIComponent("#/campaign/miyagi-point")}`;
      }

      onClickCancel();
    }, [onClickCancel, navigate]);

    return (
      <div
        className={css({
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bg: "rgba(0, 0, 0, 0.1)",
          zIndex: 888,
        })}
      >
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <div
          className={css({
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 999,
            backdropFilter: "blur(3px)",
            transition: "transform 0.3s ease-out",
            transform: "translateY(100%)",
            animation: "slide-up 0.3s forwards",
          })}
          onClick={handleOutsideClick}
        >
          <div
            className={css({
              bg: "white",
              roundedTop: "2xl",
              px: "16px",
              pt: "16px",
              pb: "48px",
              display: "flex",
              flexDir: "column",
              gap: "16px",
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 999,
            })}
          >
            <div>
              <div
                className={css({
                  display: "flex",
                })}
              >
                <div
                  className={css({
                    color: "text.primary",
                    fontSize: "18px",
                    fontWeight: 500,
                  })}
                >
                  {t("しろいし生活応援ポイントに応募しましょう！")}
                </div>

                <button
                  type="button"
                  className={css({
                    color: "text.primary",
                    border: "none",
                    cursor: "pointer",
                    ml: "auto",
                    bg: "action.hover",
                    rounded: "full",
                    p: "4px",
                  })}
                  onClick={onClickCancel}
                >
                  <Close />
                </button>
              </div>
              <p
                className={css({
                  color: "text.secondary",
                  fontSize: "14px",
                })}
              >
                {t("まずは「みやぎ防災ミニアプリ」に登録")}
              </p>
            </div>
            <p>
              {t(
                "ポイント応募にはみやぎ防災ミニアプリの登録が必要です。登録完了後、その場でポイントが付与されます。",
              )}
            </p>
            <div
              className={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              })}
            >
              <img
                src={bousaiMiyapo}
                alt={t("防災アプリに登録")}
                className={css({
                  w: "176px",
                })}
              />
            </div>
            <div
              className={css({
                display: "flex",
                gap: "10px",
                flexDir: "column",
              })}
            >
              <button
                type="button"
                className={css({
                  bg: "surface.accentPrimary",
                  color: "white",
                  rounded: "2xl",
                  p: "12px",
                })}
                onClick={handleClickApplyWithBousai}
              >
                {t("防災に登録して応募する")}
              </button>
              <button
                type="button"
                className={css({
                  bg: "rgba(229, 231, 235, 0.50)",
                  rounded: "2xl",
                  p: "12px",
                })}
                onClick={onClickCancel}
              >
                {t("あとで")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
